import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { getDomainFromUrl, getDisplayDomainFromUrl, getSiteNameFromUrl } from 'utils/linkUtils';

type LinkPreviewProps = {
  href: string;
  children?: React.ReactNode;
  showFavicon?: boolean;
};

const FAVICON_BASE = 'https://www.google.com/s2/favicons?domain=';

const LinkPreview = ({ href, children, showFavicon = true }: LinkPreviewProps): React.JSX.Element => {
  const theme = useTheme();

  const normalizedHref = /^https?:\/\//i.test(href) ? href : `https://${href}`;
  const domain = getDomainFromUrl(normalizedHref);
  const siteName = getSiteNameFromUrl(normalizedHref);
  const displayText = domain ? siteName : typeof children === 'string' ? children : normalizedHref;
  const faviconUrl = domain ? `${FAVICON_BASE}${domain}&sz=64` : null;

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        {faviconUrl && (
          <Box
            component='img'
            src={faviconUrl}
            alt=''
            sx={{ width: 24, height: 24, borderRadius: 1 }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <Typography variant='paragraphMedium'>{displayText}</Typography>
      </Box>
      <Typography
        variant='captionRegular'
        sx={{
          wordBreak: 'break-all',
          color: 'text.secondary',
        }}
      >
        {href}
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement='top'
      enterDelay={300}
      leaveDelay={100}
      disableInteractive={false}
      slotProps={{
        popper: {
          sx: {
            maxWidth: 320,
            '& .MuiTooltip-tooltip': {
              bgcolor: 'backdrop.light',
              backdropFilter: 'blur(8px)',
              color: 'text.primary',
              boxShadow: theme.shadows[13],
            },
          },
        },
      }}
    >
      <Box component='span' sx={{ display: 'inline' }}>
        <Box
          component='a'
          href={normalizedHref}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={`Link to ${normalizedHref}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.25),
            color: theme.palette.text.secondary,
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {showFavicon && faviconUrl && (
            <Box
              component='img'
              src={faviconUrl}
              alt=''
              sx={{ width: 18, height: 18, borderRadius: '50%' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <Typography
            variant='paragraphRegular'
            component='span'
            sx={{
              verticalAlign: 'middle',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textDecorationColor: theme.palette.border.main,
              color: 'inherit',
            }}
          >
            {getDisplayDomainFromUrl(normalizedHref)}
          </Typography>
          <ArrowOutwardRoundedIcon fontSize='inherit' color='inherit' />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default LinkPreview;
